<script setup lang="ts">
import { Switch } from '~/layers/base/components/ui/switch'
import { useFilters } from '~/layers/base/composables/useFilters'
import { computed } from 'vue'

const props = defineProps({
  filter: {
    type: Object,
    required: true
  },
  activeValue: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:value'])

const { getFilterOptionCount } = useFilters()

// Get count of vehicles for a filter option
const getOptionCount = (optionValue: string): number => {
  if (!props.filter) return 0
  return getFilterOptionCount(props.filter.name, optionValue)
}

// Filter options to only show those with count > 0
const filteredOptions = computed(() => {
  if (!props.filter || !props.filter.options || !Array.isArray(props.filter.options)) {
    if (!props.filter?.options) {
       console.warn(`[FilterSwitches] Filter "${props.filter?.name}" has no options property.`);
    } else if (!Array.isArray(props.filter.options)) {
       console.warn(`[FilterSwitches] Filter "${props.filter?.name}" options is not an array:`, props.filter.options);
    }
    return []
  }
  
  return props.filter.options;
})

// Check if an option is selected
const isOptionSelected = (option: any) => {
  const value = Array.isArray(option.value) ? option.value[0] : option.value
  return Array.isArray(props.activeValue) && props.activeValue.includes(value)
}

// Toggle an option selection
const toggleOption = (option: any) => {
  const value = Array.isArray(option.value) ? option.value[0] : option.value
  if (!Array.isArray(props.activeValue)) {
    emit('update:value', [value])
    return
  }
  
  if (isOptionSelected(option)) {
    const newValue = props.activeValue.filter(v => v !== value)
    emit('update:value', newValue.length ? newValue : undefined)
  } else {
    emit('update:value', [...props.activeValue, value])
  }
}

// Get display value from option
const getDisplayValue = (option: any): string => {
  if (Array.isArray(option.displayValue)) {
    return option.displayValue[0]
  }
  if (Array.isArray(option.value)) {
    return formatDisplayValue(option.value[0])
  }
  return formatDisplayValue(option.value)
}

// Format display value to be more readable
const formatDisplayValue = (value: string): string => {
  if (!value) return ''
  
  // Split by hyphens and capitalize each word
  return value.split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
</script>

<template>
  <div class="filter-switches space-y-2">
    <button 
      v-for="option in filteredOptions" 
      :key="getDisplayValue(option)"
      class="w-full flex items-center justify-between p-3 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
      type="button"
      @click="toggleOption(option)"
    >
      <div class="flex flex-col gap-0.5 text-left">
        <span class="text-sm font-medium text-foreground">{{ getDisplayValue(option) }}</span>
        <span class="text-xs text-muted-foreground">{{ getOptionCount(Array.isArray(option.value) ? option.value[0] : option.value) }} vehicles</span>
      </div>
      <Switch
        :model-value="isOptionSelected(option)"
        class="pointer-events-none data-[state=checked]:bg-primary"
      />
    </button>
  </div>
</template> 